import { createAsyncThunk } from "@reduxjs/toolkit";
import { apiConnector } from "../apiConnector";
import { toast } from "react-toastify";
import Cookies from "js-cookie";
import { endpoint } from "../apis";

// Fetch invites from API
export const fetchInvites = createAsyncThunk(
  "invites/fetchInvites",
  async (_, { rejectWithValue, getState }) => {
    try {
      const token = Cookies.get("authToken");
      // Get email from Redux store using getState
      const email = getState().auth.user?.email;
      
      if (!email) {
        return rejectWithValue("User email not found");
      }
      
      const payload = {
        action: "status",
        email: email
      };
      
      const response = await apiConnector(
        "post",
        endpoint.INVITE_API_ENDPOINT,
        payload,
        { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      );
      
      if (response.data && response.data.Response) {
        // Check if Response is an error object (not an array)
        if (response.data.Response.error) {
          // This is not an error, just no data yet
          return [];
        }
        
        // Check if Response is an array
        if (Array.isArray(response.data.Response)) {
          // Transform API response to match component expectations
          const transformedInvites = response.data.Response.map(invite => ({
            id: invite.invite_id,
            invite_id: invite.invite_id,
            email: invite.email,
            campaign_id: invite.campaign_id,
            campaignTitle: invite.campaignTitle || 'Untitled Campaign',
            brandName: invite.brandName || 'Unknown Brand',
            payout: invite.payout || 0,
            deadline: invite.deadline || '2025-12-31',
            status: (invite.status || 'pending').toUpperCase(),
            placements: invite.placements || {
              in_content_ad_placements: [],
              placement_details: {}
            },
            instructions: invite.instructions || 'N/A',
            videoUrl: invite.videoUrl || '',
            verificationStatus: invite.verificationStatus || '',
            verificationMessage: invite.verificationMessage || '',
            resubmissionsLeft: invite.resubmissionsLeft !== undefined ? invite.resubmissionsLeft : 3,
            channel_id: invite?.channel_id,
            channel_name: invite.channel_name,
            campaignAim: invite.campaignAim || '',
            usps: invite.usps || [],
            socialMediaPlatforms: invite.socialMediaPlatforms || [],
            audiencePersona: invite.audiencePersona || [],
            regions: invite.regions || [],
            audienceTarget: invite.audienceTarget || '',
            budget: invite.budget || 0,
            productLink: invite.productLink || '',
            contentUrl: invite.contentUrl || {}
          }));
          
          return transformedInvites;
        } else {
          // Response is not an array, return empty array
          return [];
        }
      } else {
        return rejectWithValue("Unexpected response format from API");
      }
    } catch (error) {
      console.error("Error fetching invites:", error);
      return rejectWithValue(error.response?.data || error.message || "Failed to fetch invites");
    }
  }
);

// Accept invite
export const acceptInvite = createAsyncThunk(
  "invites/acceptInvite",
  async (inviteId, { rejectWithValue, getState }) => {
    try {
      const token = Cookies.get("authToken");
      
      const payload = {
        action: "update",
        invite_id: inviteId,
        status: "accepted"
      };
      
      const response = await apiConnector(
        "post",
        endpoint.INVITE_API_ENDPOINT,
        payload,
        { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      );
      
      if (response.data && response.data.Response) {
        toast.success('Invite accepted successfully!');
        return { inviteId, status: 'ACCEPTED' };
      } else {
        return rejectWithValue("Unexpected response format");
      }
    } catch (error) {
      console.error("Error accepting invite:", error);
      toast.error('Failed to accept invite');
      return rejectWithValue(error.response?.data || error.message || "Failed to accept invite");
    }
  }
);

// Reject invite
export const rejectInvite = createAsyncThunk(
  "invites/rejectInvite",
  async (inviteId, { rejectWithValue, dispatch, getState }) => {
    try {
      const token = Cookies.get("authToken");
      
      const payload = {
        action: "update",
        invite_id: inviteId,
        status: "rejected"
      };
      
      const response = await apiConnector(
        "post",
        endpoint.INVITE_API_ENDPOINT,
        payload,
        { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      );
      
      if (response.data && response.data.Response) {
        toast.success('Invite rejected successfully');
        // Refetch the updated invites list
        dispatch(fetchInvites());
        return { inviteId, status: 'REJECTED' };
      } else {
        return rejectWithValue("Unexpected response format");
      }
    } catch (error) {
      console.error("Error rejecting invite:", error);
      toast.error('Failed to reject invite');
      return rejectWithValue(error.response?.data || error.message || "Failed to reject invite");
    }
  }
);

// Submit video for verification
export const submitVideo = createAsyncThunk(
  "invites/submitVideo",
  async ({ inviteId, videoUrl }, { rejectWithValue, getState }) => {
    try {
      const token = Cookies.get("authToken");
      
      const payload = {
        action: "update",
        invite_id: inviteId,
        status: "ACCEPTED",
        videoUrl: videoUrl.trim()
      };
      
      const response = await apiConnector(
        "post",
        endpoint.INVITE_API_ENDPOINT,
        payload,
        { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      );
      
      if (response.data && response.data.Response) {
        toast.success('Video URL submitted successfully');
        return { 
          inviteId, 
          videoUrl, 
          status: 'ACCEPTED', 
          verificationStatus: 'UNDER_REVIEW', 
          verificationMessage: '' 
        };
      } else {
        return rejectWithValue("Unexpected response format");
      }
    } catch (error) {
      console.error("Error submitting video:", error);
      toast.error('Failed to submit video');
      return rejectWithValue(error.response?.data || error.message || "Failed to submit video");
    }
  }
);
